import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, PieChart, Pie, Cell, Tooltip, ResponsiveContainer, XAxis, YAxis, Legend,
} from 'recharts';

const COLORS = ['#4CAF50', '#F44336'];

const safeText = (val) => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    if (val.message) return String(val.message);
    if (val.assessment_name) return String(val.assessment_name);
    if (val.name && val.deadline) return `${val.name} — Due: ${new Date(val.deadline).toLocaleDateString()}`;
    if (val.name) return String(val.name);
    try { return JSON.stringify(val); } catch (e) { return 'Object'; }
  }
  return String(val);
};

const formatDeadlineItem = (item) => {
  if (!item) return '—';
  if (typeof item === 'string') return item;
  if (typeof item === 'object') {
    if (item.deadline && item.name) {
      const d = item.deadline ? new Date(item.deadline).toLocaleDateString() : 'N/A';
      return `${item.name} — Due: ${d}`;
    }
    if (item.assessment_name && item.phase) {
      const d = item.deadline ? new Date(item.deadline).toLocaleDateString() : 'N/A';
      return `${item.assessment_name} — Phase: ${item.phase} — Due: ${d}`;
    }
    if (item.message) return item.message;
    return safeText(item);
  }
  return String(item);
};

const COBITAssessmentDashboardPage = () => {
  const [domainScores, setDomainScores] = useState([]);
  const [answerStats, setAnswerStats] = useState({ yes: 0, no: 0 });
  const [overallScore, setOverallScore] = useState(null);
  const [overview, setOverview] = useState({ domains: 0, processes: 0, questions: 0 });
  const [recentActivities, setRecentActivities] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [userProfile, setUserProfile] = useState({ name: '', department: '', email: '' });
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    const fetchDashboard = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}maturity/analytics/cobit-dashboard/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setDomainScores(Array.isArray(data.domain_scores) ? data.domain_scores : []);
        setAnswerStats({ yes: data.total_yes || 0, no: data.total_no || 0 });
        setOverallScore(data.average_score ?? null);
        setOverview(data.overview || { domains: 0, processes: 0, questions: 0 });
        setRecentActivities(Array.isArray(data.recent_activities) ? data.recent_activities : []);
        setDeadlines(Array.isArray(data.deadlines) ? data.deadlines : []);
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
        setUserProfile(data.user_profile || { name: '', department: '', email: '' });

        // Use backend-provided status_label to reflect submission properly
        const assessmentsList = Array.isArray(data.your_assessments) ? data.your_assessments : [];
        const normalizedAssessments = assessmentsList.map(a => ({
          ...a,
          is_overdue: a.status_label?.includes('Overdue') || false,
          deadline: a.deadline ? new Date(a.deadline).toISOString().split('T')[0] : null,
          status_label: a.status_label || 'On Time ⏰'
        }));
        setAssessments(normalizedAssessments);

        setLoading(false);
      } catch (err) {
        console.error('COBIT dashboard load error:', err?.response || err);
        if (err.response?.status === 401) window.location.href = '/login';
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) return <div className="text-center mt-10 text-[#FF4D00] font-medium">Loading dashboard...</div>;

  return (
    <div className="bg-white min-h-screen px-6 py-8 max-w-7xl mx-auto text-gray-800">
      {/* Welcome Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#FF4D00]">Welcome, {userProfile.name || 'User'}!</h1>
        <p className="text-sm text-gray-600">Department: {userProfile.department || 'N/A'}</p>
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-4 mb-8">
        <button
          onClick={() => window.location.href = '/cobit/assessment'}
          className="bg-[#FF4D00] text-white px-4 py-2 rounded hover:bg-orange-600 transition"
        >
          Start Assessment
        </button>
        <button
          onClick={() => window.location.href = '/cobit/report'}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
        >
          View Report
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-orange-100 p-5 rounded-lg shadow hover:shadow-md transition text-center">
          <p className="text-lg font-semibold">Domains</p>
          <p className="text-3xl text-[#FF4D00]">{overview.domains}</p>
        </div>
        <div className="bg-white border border-orange-100 p-5 rounded-lg shadow hover:shadow-md transition text-center">
          <p className="text-lg font-semibold">Processes</p>
          <p className="text-3xl text-[#FF4D00]">{overview.processes}</p>
        </div>
        <div className="bg-white border border-orange-100 p-5 rounded-lg shadow hover:shadow-md transition text-center">
          <p className="text-lg font-semibold">Questions</p>
          <p className="text-3xl text-[#FF4D00]">{overview.questions}</p>
        </div>
      </div>

      {/* Overall Score */}
      <div className="bg-orange-50 p-6 rounded-lg shadow text-center mb-10">
        <p className="text-lg font-semibold">Overall Maturity Score</p>
        <p className="text-5xl font-bold text-[#FF4D00]">{overallScore ?? 'N/A'}</p>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div className="bg-white p-5 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-[#FF4D00] mb-2">Domain Maturity Levels</h2>

          {domainScores.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={domainScores.map((d) => ({
                  name: `${d.code || d.domain || d.domain_name || d.title || "Unknown"}`,
                  score: typeof d.score === "number" ? d.score : Number(d.score) || 0,
                }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
              >
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  height={90}       // more space for labels
                  style={{ fontSize: 10 }}
                />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="score" fill="#FF4D00" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-sm">No domain scores available.</p>
          )}
        </div>
        <div className="bg-white p-5 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-[#FF4D00] mb-2">Response Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Yes', value: answerStats.yes },
                  { name: 'No', value: answerStats.no },
                ]}
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
                dataKey="value"
              >
                {COLORS.map((color, i) => <Cell key={i} fill={color} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity & Deadlines */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div className="bg-white p-5 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-[#FF4D00] mb-2">Recent Activity</h2>
          <ul className="list-disc pl-5 text-gray-700 text-sm">
            {recentActivities.length > 0 ? recentActivities.map((item, i) => <li key={i}>{safeText(item)}</li>) : <li>No recent activity</li>}
          </ul>
        </div>
        <div className="bg-white p-5 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-[#FF4D00] mb-2">Upcoming Deadlines</h2>
          <ul className="list-disc pl-5 text-gray-700 text-sm">
            {deadlines.length > 0 ? deadlines.map((item, i) => <li key={i}>{formatDeadlineItem(item)}</li>) : <li>No upcoming deadlines</li>}
          </ul>
        </div>
      </div>

      {/* Your Assessments */}
      <div className="bg-white p-5 rounded-lg shadow mb-10">
        <h2 className="text-lg font-semibold text-[#FF4D00] mb-2">Your Assessments</h2>
        <ul className="list-disc pl-5 text-gray-700 text-sm">
          {assessments.length > 0 ? assessments.map(a => (
            <li key={a.assessment_id || a.id || `${a.name}-${a.deadline}`} className="mb-2 flex items-center justify-between">
              <span className={a.is_overdue ? 'text-red-600 font-semibold' : ''}>
                {a.name} — Due: {a.deadline || 'N/A'}
              </span>
              <span className={`ml-2 px-2 py-0.5 rounded text-white text-xs ${a.is_overdue ? 'bg-red-600' : 'bg-green-500'}`}>
                {a.status_label}
              </span>
            </li>
          )) : <li className="text-gray-400">No assessments available</li>}
        </ul>
      </div>

      {/* Notifications */}
      <div className="bg-white p-5 rounded-lg shadow mb-10">
        <h2 className="text-lg font-semibold text-[#FF4D00] mb-2">Notifications</h2>
        <ul className="list-disc pl-5 text-gray-700 text-sm">
          {notifications.length > 0 ? notifications.map((n, i) => <li key={i}>{safeText(n)}</li>) : <li>No notifications</li>}
        </ul>
      </div>

      {/* User Profile */}
      <div className="bg-white p-5 rounded-lg shadow mb-10">
        <h2 className="text-lg font-semibold text-[#FF4D00] mb-2">User Profile</h2>
        <p><strong>Name:</strong> {userProfile.name}</p>
        <p><strong>Department:</strong> {userProfile.department}</p>
        <p><strong>Email:</strong> {userProfile.email}</p>
      </div>

      {/* Help Link */}
      <div className="text-center">
        <a href="/contact" className="text-[#FF4D00] underline hover:text-orange-700">
          Need help? Visit our support page
        </a>
      </div>
    </div>
  );
};

export default COBITAssessmentDashboardPage;
